<template>
  <div>
    <!-- Level complete overlay -->
    <LevelComplete v-if="gameCompleted" />

    <div v-else class="career-game">
      <!-- Career selection -->
      <h1 v-if="!currentCareer" class="instruction">Career Day! Choose your job</h1>
      <div v-if="!currentCareer" class="career-options">
        <button class="career-card" @click="selectCareer('chef')">
          <div class="emoji">👩‍🍳</div>
          <div class="label">Chef</div>
          <div class="hint">Make a pizza and bake it!</div>
        </button>
        <button class="career-card" @click="selectCareer('farmer')">
          <div class="emoji">👩‍🌾</div>
          <div class="label">Farmer</div>
          <div class="hint">Harvest crops for your basket!</div>
        </button>
      </div>

      <!-- Chef: Pizza game -->
      <div v-else-if="currentCareer === 'chef'" class="game-area chef-area">
        <h2 class="instruction">Make your pizza!</h2>

        <div class="game-row">
          <!-- Ingredient list (hidden once pizza is full) -->
          <div
            class="item-list"
            v-if="chef.pizzaItems.length < chef.maxIngredients"
          >
            <div
              v-for="(item, index) in chef.availableIngredients"
              :key="'ing-' + index"
              class="draggable-item"
              :draggable="true"
              :style="draggedItem === item ? draggedStyle : null"
              @dragstart="handleDragStart($event, item, 'chef-ingredient')"
              @click="speakOutLoud(item.name)"
              @touchstart.prevent="handleTouchStart($event, item, 'chef-ingredient')"
              @touchmove.prevent="handleTouchMove"
              @touchend="handleTouchEnd"
            >
              {{ getItemDisplay(item) }}
            </div>
          </div>

          <!-- Pizza and Oven -->
          <div class="pizza-and-oven">
            <!-- Pizza base (also becomes draggable pizza when complete) -->
            <div class="pizza-area">
              <h3 class="sub-instruction">🍕 Your Pizza ({{ chef.pizzaItems.length }}/{{ chef.maxIngredients }})</h3>
              <div
                class="pizza-base"
                :class="{ canDrag: chef.pizzaItems.length === chef.maxIngredients }"
                :style="[getPizzaBaseStyle, (draggedItem && draggedItem.name === 'Pizza') ? draggedStyle : null]"
                :draggable="chef.pizzaItems.length === chef.maxIngredients"
                @dragstart="chef.pizzaItems.length === chef.maxIngredients ? handleDragStart($event, { name: 'Pizza' }, 'chef-pizza') : null"
                @touchstart.prevent="chef.pizzaItems.length === chef.maxIngredients ? handleTouchStart($event, { name: 'Pizza' }, 'chef-pizza') : null"
                @touchmove.prevent="handleTouchMove"
                @touchend="handleTouchEnd"
                @dragover.prevent
                @drop="handleDrop($event)"
              >
                <div class="pizza-items">
                  <div
                    v-for="(item, index) in chef.pizzaItems"
                    :key="'top-' + index"
                    class="pizza-item"
                    :style="getPizzaItemStyle(index)"
                  >
                    {{ getItemDisplay(item) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Oven area appears only when pizza has all 7 toppings -->
            <div class="oven-area" v-if="chef.pizzaItems.length === chef.maxIngredients">
              <h3 class="sub-instruction">🔥 Drag your pizza to the oven!</h3>
              <div
                class="oven"
                :style="getOvenStyle"
                @dragover.prevent
                @drop="handleDrop($event)"
              >
                <img :src="chef.ovenImageUrl" alt="Oven" />
                <div class="oven-label">Oven</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Farmer: Harvest game -->
      <div v-else-if="currentCareer === 'farmer'" class="game-area farmer-area">
        <h2 class="instruction">Harvest your crops!</h2>

        <div class="goals">
          <span
            v-for="(c, idx) in farmer.cropDefinitions"
            :key="'goal-' + idx"
            class="goal-chip"
          >
            {{ getCropEmoji(c.name) }} {{ c.name }}: {{ farmer.collected[c.name] || 0 }}/{{ c.target }}
          </span>
        </div>

        <div class="field-and-basket">
          <div class="field" @dragover.prevent>
            <div
              v-for="crop in farmer.fieldCrops"
              :key="crop.id"
              class="crop-item"
              :style="[draggedItem === crop ? draggedStyle : { left: crop.x + '%', top: crop.y + '%'}]"
              :draggable="true"
              @dragstart="handleDragStart($event, crop, 'farmer-crop')"
              @touchstart.prevent="handleTouchStart($event, crop, 'farmer-crop')"
              @touchmove.prevent="handleTouchMove"
              @touchend="handleTouchEnd"
            >
              <img :src="crop.imageUrl" :alt="crop.name" />
            </div>
          </div>

          <div class="basket-area">
            <div
              class="basket"
              @dragover.prevent
              @drop="handleDrop($event)"
            >
              🧺
              <div class="basket-label">Basket</div>
            </div>
          </div>
        </div>

        <div class="progress">
          Collected: {{ totalCollected }}/{{ totalTarget }}
        </div>
      </div>

      <!-- Feedback -->
      <p v-if="message" class="message">{{ message }}</p>
    </div>
  </div>
</template>

<script>
import LevelComplete from '../LevelComplete.vue';
import { useSpeechSynthesis } from '@/composables/useSpeechSynthesis.js';

const emojiMapping = {
  // From original
  Eggs: "🍳",
  Toast: "🍞",
  Bacon: "🥓",
  Pancakes: "🥞",
  Cereal: "🥣",
  Milk: "🥛",
  Juice: "🧃",
  Oatmeal: "🥣",
  Yogurt: "🥛",
  Apple: "🍎",
  Sandwich: "🥪",
  Salad: "🥗",
  Soup: "🍲",
  Pasta: "🍝",
  Rice: "🍚",
  Chicken: "🍗",
  Fish: "🐟",
  Vegetables: "🥦",
  Cake: "🍰",
  "Ice Cream": "🍦",
  Pie: "🥧",
  Pudding: "🍮",
  StrawBerry: "🍓",
  Cookies: "🍪",
  Brownies: "🍫",

  // Career Day additions
  Sauce: "🍅",
  Cheese: "🧀",
  Pepperoni: "🍖",
  Mushroom: "🍄",
  Mushrooms: "🍄",
  Olives: "🫒",
  "Bell Pepper": "🫑",
  Onion: "🧅",
  Basil: "🌿",
  Pineapple: "🍍",
  Pizza: "🍕",
  Lettuce: "🥬",
  Carrot: "🥕",
  Corn: "🌽",
  Strawberry: "🍓",
};

export default {
  name: "CareerDayGame",
  components: { LevelComplete },
  setup() {
    const { speakOutLoud } = useSpeechSynthesis();
    return { speakOutLoud };
  },
  data() {
    return {
      // Game state
      currentCareer: null,
      gameCompleted: false,

      // UI/UX
      message: "",
      draggedItem: null,
      draggedStyle: {},
      dragContext: null,

      // Chef config/state
      chef: {
        doughImageUrl: "https://static.vecteezy.com/system/resources/previews/025/901/595/non_2x/ready-made-pizza-dough-with-tomato-sauce-about-picking-pizza-baked-baked-dough-for-collecting-pizza-preparation-with-ketchup-cartoon-vector.jpg",
        ovenImageUrl: "https://static.vecteezy.com/system/resources/previews/025/549/833/non_2x/antique-pizza-oven-drawing-cute-cartoon-style-illustration-vector.jpg",
        maxIngredients: 7,
        availableIngredients: [
          { name: "Sauce" },
          { name: "Cheese" },
          { name: "Pepperoni" },
          { name: "Mushroom" },
          { name: "Olives" },
          { name: "Bell Pepper" },
          { name: "Onion" },
          { name: "Basil" },
          { name: "Pineapple" },
        ],
        pizzaItems: [],
      },

      // Farmer config/state
      farmer: {
        cropDefinitions: [
          {
            name: "Lettuce",
            target: 3,
            imageUrl: "https://twemoji.maxcdn.com/v/latest/72x72/1f96c.png",
          },
          {
            name: "Carrot",
            target: 4,
            imageUrl: "https://twemoji.maxcdn.com/v/latest/72x72/1f955.png",
          },
          {
            name: "Corn",
            target: 3,
            imageUrl: "https://twemoji.maxcdn.com/v/latest/72x72/1f33d.png",
          },
        ],
        fieldCrops: [], // { id, name, imageUrl, x, y }
        collected: {},  // { [name]: number }
        nextId: 1,
      },
    };
  },
  computed: {
    totalTarget() {
      return this.farmer.cropDefinitions.reduce((sum, c) => sum + c.target, 0);
    },
    totalCollected() {
      return this.farmer.cropDefinitions.reduce(
        (sum, c) => sum + (this.farmer.collected[c.name] || 0),
        0
      );
    },
    getPizzaBaseStyle() {
      const bg = this.chef.doughImageUrl
        ? `url('${this.chef.doughImageUrl}')`
        : "radial-gradient(circle at 30% 30%, #f8e0a0, #e3c07b)";
      return {
        backgroundImage: bg,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    },
    getOvenStyle() {
      return {};
    },
  },
  methods: {
    // Shared
    getItemDisplay(item) {
      return emojiMapping[item.name] || item.name;
    },
    getCropEmoji(name) {
      return emojiMapping[name] || "🌱";
    },

    // Flow
    selectCareer(career) {
      this.currentCareer = career;
      this.message = "";
      if (career === "chef") {
        this.speakOutLoud("Make your pizza!");
      } else if (career === "farmer") {
        this.initFieldIfNeeded();
        this.speakOutLoud("Harvest your crops!");
      }
    },
    backToCareers() {
      this.currentCareer = null;
    },
    markCareerComplete() {
      this.gameCompleted = true;
      this.playSound("correct");
      this.speakOutLoud("Great job!");
      setTimeout(() => {
        if (this.$router) this.$router.push("/professions");
      }, 4000);
    },

    // Chef logic
    addIngredientToPizza(item) {
      const already = this.chef.pizzaItems.find(i => i.name === item.name);
      if (already) {
        this.message = `❌ ${item.name} is already on your pizza.`;
        this.playSound("incorrect");
      } else if (this.chef.pizzaItems.length >= this.chef.maxIngredients) {
        this.message = "❌ Max toppings reached.";
        this.playSound("incorrect");
      } else {
        this.chef.pizzaItems.push({ name: item.name });
        this.chef.availableIngredients = this.chef.availableIngredients.filter(i => i.name !== item.name);
        this.message = `✅ Added ${item.name}!`;
        this.playSound("correct");
        // Say the name on successful drop
        this.speakOutLoud(item.name);
      }
      setTimeout(() => (this.message = ""), 1200);
    },
    bakePizza() {
      if (this.chef.pizzaItems.length < this.chef.maxIngredients) {
        this.message = "❌ Add all 7 toppings before baking.";
        this.playSound("incorrect");
        setTimeout(() => (this.message = ""), 1200);
        return;
      }
      this.message = "🔥 Baking your pizza...";
      this.playSound("correct");
      setTimeout(() => {
        this.message = "🎉 Delicious pizza!";
        this.speakOutLoud("Pizza is ready!");
        setTimeout(() => {
          this.message = "";
          this.markCareerComplete();
        }, 800);
      }, 900);
    },
    getPizzaItemStyle(index) {
      const total = this.chef.pizzaItems.length || 1;
      const angleIncrement = (2 * Math.PI) / total;
      const angle = index * angleIncrement - Math.PI / 2;
      const radius = 31;
      const x = 50 + radius * Math.cos(angle);
      const y = 50 + radius * Math.sin(angle);
      return {
        position: "absolute",
        left: x + '%',
        top: y + '%',
        transform: "translate(-50%, -50%)",
      };
    },

    // Farmer logic
    initFieldIfNeeded() {
      if (this.farmer.fieldCrops.length === 0) {
        this.buildField();
        this.farmer.collected = {};
      }
    },
    buildField() {
      const items = [];
      let id = 1;
      this.farmer.cropDefinitions.forEach(c => {
        const count = c.target + 3; // extras
        for (let i = 0; i < count; i++) {
          items.push({
            id: id++,
            name: c.name,
            imageUrl: c.imageUrl,
            x: 10 + Math.random() * 80,
            y: 10 + Math.random() * 80,
          });
        }
      });
      this.farmer.fieldCrops = items;
    },
    canCollectCrop(name) {
      const def = this.farmer.cropDefinitions.find(c => c.name === name);
      if (!def) return false;
      const have = this.farmer.collected[name] || 0;
      return have < def.target;
    },
    addCropToBasket(crop) {
      if (!this.canCollectCrop(crop.name)) {
        this.message = `❌ You already have enough ${crop.name}.`;
        this.playSound("incorrect");
        setTimeout(() => (this.message = ""), 1200);
        return;
      }
      const index = this.farmer.fieldCrops.findIndex(i => i.id === crop.id);
      if (index >= 0) this.farmer.fieldCrops.splice(index, 1);
      this.farmer.collected[crop.name] = (this.farmer.collected[crop.name] || 0) + 1;
      this.message = `✅ Picked ${crop.name}!`;
      this.playSound("correct");
      // Say the name on successful drop
      this.speakOutLoud(crop.name);
      setTimeout(() => (this.message = ""), 900);

      const allDone = this.farmer.cropDefinitions.every(
        c => (this.farmer.collected[c.name] || 0) >= c.target
      );
      if (allDone) {
        this.message = "🎉 Harvest complete! Farmer day done.";
        setTimeout(() => {
          this.message = "";
          this.markCareerComplete();
        }, 900);
      }
    },

    // Drag and Touch handlers
    handleDragStart(event, item, context) {
      const payload = { ctx: context, item };
      if (event.dataTransfer) {
        event.dataTransfer.setData("text/plain", JSON.stringify(payload));
      }
    },
    handleDrop(event) {
      if (!event.dataTransfer) return;
      const itemData = event.dataTransfer.getData("text/plain");
      let data;
      try {
        data = JSON.parse(itemData);
      } catch (e) {
        console.error("Invalid item data");
        return;
      }

      const dropTarget = event.target;

      if (data.ctx === "chef-ingredient") {
        if (dropTarget && (dropTarget.classList.contains("pizza-base") || dropTarget.closest(".pizza-base"))) {
          this.addIngredientToPizza(data.item);
        } else {
          this.message = "❌ Drop toppings on the pizza.";
          this.playSound("incorrect");
          setTimeout(() => (this.message = ""), 1200);
        }
      } else if (data.ctx === "chef-pizza") {
        if (dropTarget && (dropTarget.classList.contains("oven") || dropTarget.closest(".oven"))) {
          this.bakePizza();
        } else {
          this.message = "❌ Drop the pizza on the oven.";
          this.playSound("incorrect");
          setTimeout(() => (this.message = ""), 1200);
        }
      } else if (data.ctx === "farmer-crop") {
        if (dropTarget && (dropTarget.classList.contains("basket") || dropTarget.closest(".basket"))) {
          this.addCropToBasket(data.item);
        } else {
          this.message = "❌ Drop crops into the basket.";
          this.playSound("incorrect");
          setTimeout(() => (this.message = ""), 1200);
        }
      }
    },
    handleTouchStart(event, item, context) {
      event.preventDefault();
      this.draggedItem = item;
      this.dragContext = context;

      const touch = event.touches[0];
      if (!touch) return;

      this.draggedStyle = {
        position: 'fixed',
        left: touch.clientX + 'px',
        top: touch.clientY + 'px',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        pointerEvents: 'none',
      };
      // Note: no speech here; we only speak on successful drop
    },
    handleTouchMove(event) {
      event.preventDefault();
      if (!this.draggedItem) return;
      const touch = event.touches[0];
      if (!touch) return;
      this.draggedStyle.left = touch.clientX + 'px';
      this.draggedStyle.top = touch.clientY + 'px';
    },
    handleTouchEnd(event) {
      event.preventDefault();
      if (!this.draggedItem) {
        this.draggedStyle = {};
        this.dragContext = null;
        return;
      }

      const touch = event.changedTouches[0];
      if (!touch) {
        this.draggedItem = null;
        this.draggedStyle = {};
        this.dragContext = null;
        return;
      }

      const dropTarget = document.elementFromPoint(touch.clientX, touch.clientY);

      if (this.dragContext === "chef-ingredient") {
        if (dropTarget && (dropTarget.classList.contains("pizza-base") || dropTarget.closest(".pizza-base"))) {
          this.addIngredientToPizza(this.draggedItem);
        } else {
          this.message = "❌ Drop toppings on the pizza.";
          this.playSound("incorrect");
          setTimeout(() => (this.message = ""), 1200);
        }
      } else if (this.dragContext === "chef-pizza") {
        if (dropTarget && (dropTarget.classList.contains("oven") || dropTarget.closest(".oven"))) {
          this.bakePizza();
        } else {
          this.message = "❌ Drop the pizza on the oven.";
          this.playSound("incorrect");
          setTimeout(() => (this.message = ""), 1200);
        }
      } else if (this.dragContext === "farmer-crop") {
        if (dropTarget && (dropTarget.classList.contains("basket") || dropTarget.closest(".basket"))) {
          this.addCropToBasket(this.draggedItem);
        } else {
          // No tap-to-collect; must drop correctly
          this.message = "❌ Drop crops into the basket.";
          this.playSound("incorrect");
          setTimeout(() => (this.message = ""), 1200);
        }
      }

      this.draggedItem = null;
      this.draggedStyle = {};
      this.dragContext = null;
    },

    // Sounds
    playSound(type) {
      let url;
      if (type === "correct") {
        url = "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a";
      } else if (type === "incorrect") {
        url = "https://codeskulptor-demos.commondatastorage.googleapis.com/pang/arrow.mp3";
      }
      if (url) {
        const audio = new Audio(url);
        audio.play().catch(() => {});
      }
    },
  },
};
</script>

<style scoped>
.career-game {
  text-align: center;
  padding-bottom: 24px;
}

.instruction {
  font-size: 2rem;
  margin-bottom: 20px;
}

.sub-instruction {
  font-size: 1.25rem;
  margin-bottom: 10px;
  color: #555;
}

/* Career options */
.career-options {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}
.career-card {
  border: none;
  background: linear-gradient(180deg, #ffe9f7, #e8f6ff);
  padding: 20px 28px;
  border-radius: 16px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0,0,0,0.08);
  transition: transform .2s ease, box-shadow .2s ease;
  min-width: 220px;
}
.career-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(0,0,0,0.12);
}
.career-card .emoji {
  font-size: 3rem;
}
.career-card .label {
  font-size: 1.4rem;
  font-weight: 700;
  margin-top: 8px;
}
.career-card .hint {
  font-size: .95rem;
  color: #666;
  margin-top: 4px;
}

/* Shared list styles */
.game-area {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.game-row {
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  align-items: center;
}
@media (min-width: 900px) {
  .game-row {
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
  }
}

.item-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 10px;
  gap: 10px;
  max-width: 780px;
}

.draggable-item {
  margin: 6px;
  padding: 14px;
  background-color: #f3e5f5;
  border-radius: 10px;
  cursor: grab;
  font-size: 2.2rem;
  user-select: none;
  transition: transform 0.3s, box-shadow .2s;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}
.draggable-item:hover {
  transform: scale(1.08);
}
.draggable-item:active {
  cursor: grabbing;
}
.draggable-item[style*="position: fixed"] {
  pointer-events: none;
}

/* Chef layout */
.pizza-and-oven {
  display: grid;
  gap: 18px;
  grid-template-columns: 1fr;
  align-items: start;
}
@media (min-width: 900px) {
  .pizza-and-oven {
    grid-template-columns: 360px 320px;
    gap: 32px;
  }
}

.pizza-area {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pizza-base {
  width: 310px;
  height: 310px;
  border: 2px solid #d0a46a;
  border-radius: 50%;
  position: relative;
  background-color: #f9e2b3;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  outline-offset: -50px;
  outline: 5px solid #f1d8a7;
  box-shadow: inset 0 0 60px rgba(0,0,0,0.08);
}
.pizza-base.canDrag {
  cursor: grab;
}
.pizza-items {
  position: relative;
  width: 100%;
  height: 100%;
}
.pizza-item {
  position: absolute;
  font-size: 3.8rem; /* Bigger toppings */
  display: flex;
  align-items: center;
  justify-content: center;
}

.oven-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.oven {
  width: 260px;
  height: 200px;
  border: 2px dashed #999;
  background: #fafafa;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-shadow: inset 0 0 24px rgba(0,0,0,0.04);
  position: relative;
}
.oven img {
  max-width: 120px;
  max-height: 120px;
}
.oven-label {
  position: absolute;
  margin-top: 140px;
  color: #555;
  font-weight: 600;
}

/* Farmer layout */
.goals {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 12px;
}
.goal-chip {
  background: #eef7ee;
  border: 1px solid #cfe6cf;
  color: #316631;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: .95rem;
}

.field-and-basket {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  align-items: start;
}
@media (min-width: 900px) {
  .field-and-basket {
    grid-template-columns: 360px 240px;
    gap: 32px;
  }
}

.field {
  width: 320px;
  height: 320px;
  border-radius: 16px;
  border: 2px solid #7ab97a;
  background: linear-gradient(180deg, #dcf6dc, #c1e8c1);
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 0 40px rgba(0,0,0,0.06);
}
.crop-item {
  position: absolute;
  transform: translate(-50%, -50%);
  width: 56px;
  height: 56px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0,0,0,0.1);
  display: grid;
  place-items: center;
  cursor: grab;
}
.crop-item img {
  width: 40px;
  height: 40px;
}
.crop-item[style*='position: fixed'] {
  pointer-events: none;
}

.basket-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.basket {
  width: 220px;
  height: 160px;
  border: 2px dashed #9e7d49;
  background: #fff9ef;
  border-radius: 16px;
  display: grid;
  place-items: center;
  font-size: 3rem;
  box-shadow: inset 0 0 24px rgba(0,0,0,0.04);
}
.basket-label {
  margin-top: 8px;
  color: #7a5a2a;
  font-weight: 700;
}

.buttons-row {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}
.buttons-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.secondary {
  background: #f3f6ff;
  color: #3b4a7b;
  border: 1px solid #cbd6ff;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 600;
}
.secondary:hover {
  background: #e9efff;
}

/* Progress & messages */
.progress {
  margin-top: 12px;
  font-size: 1.1rem;
  color: #444;
}

.message {
  font-size: 1.3rem;
  margin-top: 18px;
}

/* Drag helpers */
.draggable-item[style*="position: fixed"],
.crop-item[style*='position: fixed'],
.pizza-base[style*='position: fixed'] {
  pointer-events: none;
}
</style>